import React, { useEffect, useRef, useState } from 'react'
import { domainsAPI } from '@hpanel/web-common/api/client'
import type { Domain, DomainLogsResponse } from '@hpanel/web-common/api/types'
import { AlertTriangle, FileText, RefreshCw, X } from 'lucide-react'

/**
 * DomainLogsPanel — modal that shows recent stdout+stderr from the
 * container backing a tenant's domain.
 *
 * Static tail only (no streaming) — the v1 panel surfaces a snapshot
 * with a refresh button and a tail-size selector. A future iteration
 * can layer SSE on top once the backend gains a streaming logs route.
 *
 * Resilience contract (CLAUDE.md §9): the component never surfaces
 * raw fetch errors. `domainsAPI.getLogs` already routes through
 * `fetchAPI` which normalizes empty/non-JSON/network errors; we just
 * keep the original `Error` message for the legitimate failure case
 * so the server's `detail` reaches the user verbatim.
 */

interface DomainLogsPanelProps {
  domain: Domain
  onClose: () => void
}

const TAIL_OPTIONS = [50, 200, 500, 1000, 2000] as const

export const DomainLogsPanel: React.FC<DomainLogsPanelProps> = ({
  domain,
  onClose,
}) => {
  const [tail, setTail] = useState<number>(200)
  const [data, setData] = useState<DomainLogsResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const preRef = useRef<HTMLPreElement | null>(null)

  const fetchLogs = async (n: number) => {
    setLoading(true)
    setError(null)
    try {
      const resp = await domainsAPI.getLogs(domain.id, n)
      setData(resp)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to read container logs')
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  // Initial load + reload on tail-size change.
  useEffect(() => {
    fetchLogs(tail)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tail])

  // Auto-scroll the pre to the bottom whenever new content arrives —
  // matches what users expect from `tail` and keeps the latest line in
  // view without forcing them to scroll on every refresh.
  useEffect(() => {
    if (preRef.current) {
      preRef.current.scrollTop = preRef.current.scrollHeight
    }
  }, [data])

  // ESC closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, loading])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={() => {
        if (!loading) onClose()
      }}
      aria-hidden="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="domain-logs-title"
        data-testid="domain-logs-panel"
        className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-5 border-b border-gray-200 dark:border-gray-800">
          <div className="min-w-0 flex items-center gap-2">
            <FileText
              className="h-4 w-4 text-gray-500"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <h2
              id="domain-logs-title"
              className="text-base font-medium text-gray-900 dark:text-gray-100 truncate"
            >
              Logs — {domain.name}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <label
              htmlFor="domain-logs-tail"
              className="text-[11px] text-gray-500 dark:text-gray-400"
            >
              Tail
            </label>
            <select
              id="domain-logs-tail"
              data-testid="domain-logs-tail-select"
              value={tail}
              onChange={(e) => setTail(Number(e.target.value))}
              disabled={loading}
              className="px-2 py-1 rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-[12px] text-gray-900 dark:text-gray-100 disabled:opacity-50"
            >
              {TAIL_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt} lines
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => fetchLogs(tail)}
              disabled={loading}
              data-testid="domain-logs-refresh"
              className="h-8 inline-flex items-center gap-1.5 px-2.5 rounded-md border border-gray-200 dark:border-gray-800 text-[12px] font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              aria-label="Refresh logs"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
                strokeWidth={1.75}
              />
              Refresh
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="h-8 w-8 rounded-md grid place-items-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 p-5">
          {error ? (
            <div
              data-testid="domain-logs-error"
              className="flex items-start gap-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-[12px] text-red-700 dark:text-red-300"
            >
              <AlertTriangle
                className="h-3.5 w-3.5 mt-0.5 flex-shrink-0"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              <span>{error}</span>
            </div>
          ) : (
            <pre
              ref={preRef}
              data-testid="domain-logs-output"
              className="h-[60vh] overflow-auto rounded-md border border-gray-200 dark:border-gray-800 bg-gray-950 text-gray-100 font-mono text-[12px] leading-[1.5] p-3 whitespace-pre"
            >
              {loading && !data
                ? 'Loading…'
                : data && data.logs
                  ? data.logs
                  : 'No output yet.'}
            </pre>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 text-[11px] text-gray-500 dark:text-gray-400 flex items-center justify-between">
          <span data-testid="domain-logs-meta">
            {data
              ? `Showing last ${data.tail} lines · backend: ${data.backend_kind} · container #${data.container_id}`
              : 'Reading recent stdout + stderr from the container backing this domain.'}
          </span>
        </div>
      </div>
    </div>
  )
}
